/**
 * Workshop version of FrameWrapper — shared frame anatomy.
 *
 * Renders accent bar, header (icon + title + status badge),
 * content area. No React Flow handles.
 */

import type { ReactNode } from "react";
import { Icons } from "@/components/ui";
import { cn } from "@/lib/cn";

/* ---------- Type color map ---------- */

const FRAME_TYPE_COLORS: Record<string, string> = {
  text: "var(--color-muted)",
  code: "var(--color-agent-coder)",
  search: "var(--color-agent-researcher)",
  tool: "var(--color-muted)",
  workflow: "var(--color-warning)",
  image: "var(--color-frame-image)",
};

export function frameTypeColor(type: string): string {
  return FRAME_TYPE_COLORS[type] ?? "var(--color-muted)";
}

/* ---------- Type icon map ---------- */

const FRAME_TYPE_ICONS: Record<string, keyof typeof Icons> = {
  text: "FileText",
  code: "Code",
  search: "Globe",
  tool: "Wrench",
  workflow: "GitBranch",
  image: "Image",
};

/* ---------- Status badge ---------- */

type FrameStatus = "idle" | "streaming" | "error" | "done";

function StatusBadge({ status }: { status: FrameStatus }) {
  if (status === "idle") return null;

  const config: Record<string, { label: string; className: string }> = {
    streaming: {
      label: "Streaming",
      className: "bg-accent/20 text-accent",
    },
    error: {
      label: "Error",
      className: "bg-danger-muted text-danger",
    },
    done: {
      label: "Done",
      className: "bg-success-muted text-success",
    },
  };

  const c = config[status];
  if (!c) return null;

  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-0.5 text-canvas-base font-medium",
        c.className,
      )}
      data-testid={`frame-status-${status}`}
    >
      {c.label}
    </span>
  );
}

/* ---------- Frame Wrapper ---------- */

type FrameType = "text" | "code" | "search" | "tool" | "workflow" | "image";

interface FrameWrapperProps {
  frameId: string;
  frameType: FrameType;
  title: string;
  status: FrameStatus;
  children: ReactNode;
  className?: string;
}

export function FrameWrapper({
  frameId,
  frameType,
  title,
  status,
  children,
  className,
}: FrameWrapperProps) {
  const accentColor = frameTypeColor(frameType);
  const iconName = FRAME_TYPE_ICONS[frameType] ?? "Circle";
  const Icon = Icons[iconName];

  return (
    <div
      className={cn(
        "denker-glass-frame flex min-w-[200px] flex-col overflow-hidden rounded-frame",
        status === "streaming" && "border-accent/60",
        status === "error" && "border-danger/60",
        className,
      )}
      data-testid={`frame-${frameType}-${frameId}`}
      data-frame-type={frameType}
      data-frame-status={status}
    >
      {/* Accent bar */}
      <div
        className="h-[3px] w-full flex-shrink-0"
        style={{ backgroundColor: accentColor }}
      />

      {/* Header */}
      <div
        className="flex items-center gap-2 border-b border-glass-stroke-faint px-3 py-2"
        data-frame-header="true"
      >
        <Icon
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{ color: accentColor }}
        />
        <span className="flex-1 truncate text-canvas-base font-medium text-primary">
          {title || frameType}
        </span>
        <StatusBadge status={status} />
      </div>

      {/* Content area */}
      <div className="scrollbar-none flex-1 overflow-auto px-3 pb-3">{children}</div>
    </div>
  );
}

export type { FrameWrapperProps, FrameType, FrameStatus };
